import { Component, ElementRef, HostListener, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormControl } from '@angular/forms';
import { ActivatedRoute, ParamMap, Router } from '@angular/router';
import { PageEvent } from '@angular/material/paginator';
import { Sort } from '@angular/material/sort';
import { MatSidenav } from '@angular/material/sidenav';
import { MatDialog } from '@angular/material/dialog';
import { combineLatest, map, Subject, take, takeUntil } from 'rxjs';
import { EventTypeService } from '../../../services/event-type.service';
import {
  EventAssociatedPrintAttributeListFilters,
  EventAssociatedPrintAttributeService,
} from '../../../services/event-associated-print-attribute.service';
import { DivisionService } from '../../../services/division.service';
import { PrintAttributeType } from '../../../models/print-attribute.model';
import { PrintAttributeDefinition } from '../../../models/print-attribute.model';
import {
  EditEventAssociatedAttributeDialogComponent,
} from '../edit-event-associated-attribute-dialog/edit-event-associated-attribute-dialog.component';

@Component({
  selector: 'app-event-associated-attributes-list',
  templateUrl: './event-associated-attributes-list.component.html',
  styleUrls: ['./event-associated-attributes-list.component.scss'],
})
export class EventAssociatedAttributesListComponent implements OnInit, OnDestroy {
  private static readonly printAttrTypeToneSlugs: Readonly<Record<string, string>> = {
    [PrintAttributeType.address]: 'address',
    [PrintAttributeType.sale_dates]: 'sale-dates',
    [PrintAttributeType.url]: 'url',
    [PrintAttributeType.printer_code]: 'printer-code',
    [PrintAttributeType.header]: 'header',
    [PrintAttributeType.creative]: 'creative',
    [PrintAttributeType.market_place]: 'market-place',
    [PrintAttributeType.offer_event]: 'offer-event',
    [PrintAttributeType.abev]: 'abev',
    [PrintAttributeType.dry_version]: 'dry-version',
  };

  @ViewChild('filterSidenav') filterSidenav!: MatSidenav;
  @ViewChild('filterSidenav', { read: ElementRef }) filterSidenavElement?: ElementRef<HTMLElement>;
  @ViewChild('filterToggleButton', { read: ElementRef }) filterToggleButton?: ElementRef<HTMLElement>;
  private destroy$ = new Subject<void>();

  divisionFilterControl = new FormControl<string[]>([]);
  attributeTypeFilterControl = new FormControl<string[]>([]);
  eventTypeFilterControl = new FormControl<string[]>([]);
  searchControl = new FormControl('');

  divisionOptions: { id: string; name: string }[] = [];
  eventTypeOptions: { id: string; name: string }[] = [];
  attributeTypeOptions: { id: string; label: string }[] = [];
  eventId = '0';
  rows: PrintAttributeDefinition[] = [];

  displayedColumns = [
    'Name',
    'EventTypeNames',
    'PrintAttributeType',
    'Description',
    'DivisionNames',
    'Versions',
    'Actions',
  ];

  viewModel$ = combineLatest([
    this.associatedService.associatedPrintAttributes$,
    this.associatedService.page$,
    this.associatedService.totalRecords$,
  ]).pipe(
    map(([attributes, page, totalRecords]) => ({
      attributes,
      page,
      totalRecords,
    })),
  );

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private eventTypeService: EventTypeService<any>,
    private divisionService: DivisionService<any>,
    private associatedService: EventAssociatedPrintAttributeService,
    private dialog: MatDialog,
  ) {}

  ngOnInit(): void {
    this.eventId = this.route.snapshot.paramMap.get('eventId') ?? '0';
    this.associatedService.setEventId(this.eventId);
    this.rebuildAttributeTypeOptions(this.route.snapshot.queryParamMap);
    this.syncListRouteToState(this.route.snapshot.queryParamMap);

    this.divisionService.divisionsList$
      .pipe(take(1), takeUntil(this.destroy$))
      .subscribe((divisions) => {
        this.divisionOptions = divisions.map((d) => ({
          id: d.Id,
          name: d.DivisionName,
        }));
      });

    this.eventTypeService.eventTypesList$
      .pipe(take(1), takeUntil(this.destroy$))
      .subscribe((types) => {
        this.eventTypeOptions = types.map((t) => ({
          id: t.Id,
          name: t.EventTypeName,
        }));
      });

    this.route.queryParamMap.pipe(takeUntil(this.destroy$)).subscribe((qp) => {
      this.syncListRouteToState(qp);
    });

    this.viewModel$.pipe(takeUntil(this.destroy$)).subscribe((vm) => {
      this.rows = vm.attributes;
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  toggleFilters(): void {
    this.filterSidenav?.toggle();
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (!this.filterSidenav?.opened) return;

    const target = event.target as HTMLElement | null;
    if (!target) return;

    if (this.filterSidenavElement?.nativeElement.contains(target)) return;
    if (this.filterToggleButton?.nativeElement.contains(target)) return;
    if (target.closest('.cdk-overlay-container')) return;

    this.filterSidenav.close();
  }

  onFilterChange(): void {
    /* filters applied explicitly via Apply */
  }

  onSearch(event: Event): void {
    this.associatedService.search((event.target as HTMLInputElement).value);
  }

  clearSearch(): void {
    this.searchControl.setValue('');
    this.associatedService.search('');
  }

  onSort(sortState: Sort): void {
    this.associatedService.sort(sortState);
  }

  onPage(pageEvent: PageEvent): void {
    this.associatedService.page(pageEvent);
  }

  applyFilters(): void {
    const filters = this.readFiltersFromControls();
    this.associatedService.setListFilters(filters);
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: {
        divisionIds: filters.divisionIds.length ? filters.divisionIds : null,
        attributeTypes: filters.attributeTypes.length ? filters.attributeTypes : null,
        eventTypeIds: filters.eventTypeIds.length ? filters.eventTypeIds : null,
      },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
    this.toggleFilters();
  }

  resetFilters(): void {
    this.patchFilterControls({
      divisionIds: [],
      attributeTypes: [],
      eventTypeIds: [],
    });
    this.associatedService.clearListFilters();
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: {
        divisionIds: null,
        attributeTypes: null,
        eventTypeIds: null,
      },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
    this.toggleFilters();
  }

  edit(record: PrintAttributeDefinition): void {
    const printAttributeId = String(record.DomainId || record.Detail?.Id || '').trim();
    if (!printAttributeId) {
      return;
    }
    const dialogRef = this.dialog.open(EditEventAssociatedAttributeDialogComponent, {
      data: {
        eventId: this.eventId,
        printAttributeId,
        printAttributeDefinition: record,
      },
    });

    dialogRef.afterClosed().subscribe((reload) => {
      if (reload) {
        this.associatedService.reload();
      }
    });
  }

  printAttrToneSlug(typeLabel: string): string {
    return EventAssociatedAttributesListComponent.printAttrTypeToneSlugs[typeLabel] ?? 'unknown';
  }

  versionsDisplay(record: PrintAttributeDefinition): string {
    const v = record.Detail?.LogicalVersions;
    if (Array.isArray(v) && v.length) {
      return v.join(', ');
    }
    return '-';
  }

  private readFiltersFromControls(): EventAssociatedPrintAttributeListFilters {
    return {
      divisionIds: [...(this.divisionFilterControl.value ?? [])],
      attributeTypes: [...(this.attributeTypeFilterControl.value ?? [])],
      eventTypeIds: [...(this.eventTypeFilterControl.value ?? [])],
    };
  }

  private patchFilterControls(f: EventAssociatedPrintAttributeListFilters): void {
    this.divisionFilterControl.setValue([...f.divisionIds], { emitEvent: false });
    this.attributeTypeFilterControl.setValue([...f.attributeTypes], { emitEvent: false });
    this.eventTypeFilterControl.setValue([...f.eventTypeIds], { emitEvent: false });
  }

  private rebuildAttributeTypeOptions(qp: ParamMap): void {
    const base = Object.values(PrintAttributeType).map((label) => ({
      id: label,
      label,
    }));
    const extraIds = qp
      .getAll('attributeTypes')
      .filter((id) => !base.some((b) => b.id === id));
    const extras = extraIds.map((id) => ({ id, label: id }));
    this.attributeTypeOptions = [...base, ...extras];
  }

  private syncListRouteToState(qp: ParamMap): void {
    this.rebuildAttributeTypeOptions(qp);
    const next: EventAssociatedPrintAttributeListFilters = {
      divisionIds: qp.getAll('divisionIds'),
      attributeTypes: qp.getAll('attributeTypes'),
      eventTypeIds: qp.getAll('eventTypeIds'),
    };
    this.patchFilterControls(next);
    this.associatedService.setListFilters(next);
  }
}
